import type { GridDataSource, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
  GRID_DEFAULT_PAGE_SIZE,
} from "@entrilia/shared/components/grid/gridHelper";
import { logError } from "@entrilia/shared/logging";
import type { GridColumnDefinition } from "../../../../api/types/fileTypes";
import { getCheckedGridSx } from "../../grid/gridStyles";
import { useFilesContext } from "../FilesContext";
import type { FilesRowModel } from "../filesState";
import FilesGridHeaderToolbar from "./FilesGridHeaderToolbar";
import { getColumns } from "./filesGridDataProvider";

interface Props {
  columnDefinitions: GridColumnDefinition[];
  dataSource: GridDataSource;
  selectedRowIds: string[];
  setSelectedRowIds: (ids: string[]) => void;
}

const getRowId = (row: FilesRowModel) => row.catalogueId;

const isRowSelectable = ({ row }: GridRowParams<FilesRowModel>) => row.uploadState === undefined;

const FilesGrid = ({ columnDefinitions, dataSource, selectedRowIds, setSelectedRowIds }: Props) => {
  const { filesState, gridApiRef } = useFilesContext();

  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(() => getColumns(columnDefinitions), [columnDefinitions]);

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[FilesGrid]");
    setDataSourceError(error);
  }, []);

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading files failed" />;
  }

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={selectedRowIds.length > 0}
      selection={[selectedRowIds, setSelectedRowIds]}
      actions={<FilesGridHeaderToolbar selectedIds={selectedRowIds} />}
    >
      <DataGrid<FilesRowModel>
        apiRef={gridApiRef}
        sx={getCheckedGridSx}
        getRowId={getRowId}
        pinnedRows={{ top: filesState.uploadingFiles }}
        dataSource={dataSource}
        columns={columns}
        lazyLoading
        paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
        onDataSourceError={handleDataSourceError}
        noRowsText="No files"
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        disableColumnReorder
        disableColumnSorting
        checkboxSelection
        rowSelectionModel={getRowSelectionModelFromIds(selectedRowIds)}
        onRowSelectionModelChange={(selection) => setSelectedRowIds(getSelectedRowIdsFromModel(selection))}
        isRowSelectable={isRowSelectable}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default FilesGrid;
